import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { agentApi, dashboardWidgetApi } from "../api/agentService";
import type { AgentConfig } from "../types/agent";
import { STALE } from "@/lib/query-constants";

export function useAgents() {
  return useQuery({
    queryKey: ["agents"],
    queryFn: agentApi.listAgents,
    refetchInterval: STALE.TEN_MIN,
  });
}

export function useAgentInsights(
  filters: {
    category?: string;
    severity?: string;
    acknowledged?: boolean;
    limit?: number;
    offset?: number;
  } = {},
) {
  return useQuery({
    queryKey: ["agent-insights", filters],
    queryFn: () => agentApi.listInsights(filters),
    refetchInterval: STALE.TEN_MIN,
  });
}

export function usePaginatedAgentInsights(filters: {
  category?: string;
  severity?: string;
  acknowledged?: boolean;
}) {
  return useInfiniteQuery({
    queryKey: ["agent-insights", "feed", filters],
    queryFn: ({ pageParam }) =>
      agentApi.listInsights({ ...filters, limit: 20, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (page, pages) => {
      const count = pages.reduce((sum, p) => sum + p.insights.length, 0);
      return page.insights.length > 0 && count < page.total ? count : undefined;
    },
    refetchInterval: STALE.TEN_MIN,
  });
}

export function useUnreadInsightCount() {
  return useQuery({
    queryKey: ["agent-insights-unread"],
    queryFn: agentApi.getUnreadCount,
    refetchInterval: STALE.ONE_MIN,
  });
}

export function useAgentRuns(limit?: number) {
  return useQuery({
    queryKey: ["agent-runs", limit],
    queryFn: () => agentApi.listRuns(limit),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === "running") ? 3000 : 15_000,
  });
}

export function useUpdateAgentConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      patch,
    }: {
      agentId: number;
      patch: {
        enabled?: boolean;
        config?: Record<string, unknown>;
        schedule?: string | null;
        name?: string;
      };
    }) => agentApi.updateAgentConfig(agentId, patch),
    onMutate: async ({
      agentId,
      patch,
    }: {
      agentId: number;
      patch: {
        enabled?: boolean;
        config?: Record<string, unknown>;
        schedule?: string | null;
        name?: string;
      };
    }) => {
      if (patch.enabled === undefined) return undefined;
      await queryClient.cancelQueries({ queryKey: ["agents"] });
      const previous = queryClient.getQueryData<AgentConfig[]>(["agents"]);
      queryClient.setQueryData<AgentConfig[]>(["agents"], (old) =>
        old?.map((agent) =>
          agent.id === agentId
            ? { ...agent, enabled: patch.enabled as boolean }
            : agent,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["agents"], context.previous);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
  });
}

export function useRunAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: number) => agentApi.runAgent(agentId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insights"] });
    },
  });
}

export function useRunAllAgents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.runAllAgents,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-runs"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insights"] });
    },
  });
}

export function useAcknowledgeInsight() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (insightId: number) => agentApi.acknowledgeInsight(insightId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-insights"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insights-unread"] });
    },
  });
}

export function useAcknowledgeAllInsights() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: agentApi.acknowledgeAllInsights,
    onMutate: () => {
      const previous = queryClient.getQueryData<number>([
        "agent-insights-unread",
      ]);
      queryClient.setQueryData<number>(["agent-insights-unread"], 0);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(["agent-insights-unread"], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-insights"] });
      queryClient.invalidateQueries({ queryKey: ["agent-insights-unread"] });
    },
  });
}

export function useInitializeAgents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (companyId: number) => agentApi.initializeAgents(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
  });
}

export function useAddDashboardWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (widget: {
      chart_spec: Record<string, unknown>;
      business_unit?: string;
      position?: number;
      size?: string;
    }) => dashboardWidgetApi.addWidget(widget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-widgets"] });
    },
  });
}

export function useDashboardWidgets() {
  return useQuery({
    queryKey: ["dashboard-widgets"],
    queryFn: dashboardWidgetApi.listWidgets,
    staleTime: STALE.ONE_MIN,
  });
}

export function useRemoveDashboardWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (widgetId: number) => dashboardWidgetApi.deleteWidget(widgetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dashboard-widgets"] });
    },
  });
}
